<template>
  <!--
    HoneyBook vs Workspace369 yearly cost (LIST #85), placed in the post with `::honeybook-cost-calculator`.
    Math and price sources: utils/honeybookCost.ts. The defaults render on the server, so crawlers and
    AI tools see a real worked example; every variant line renders hidden (v-show) so the localization
    pipeline extracts it from the English page. Rules are scoped under .hbc to beat the article's
    prose rules (.ed-prose p / a).
  -->
  <section class="hbc" aria-labelledby="hbc-title" data-sticky-cta-hide>
    <h3 id="hbc-title" class="hbc__title">Work out your own yearly cost</h3>
    <p class="hbc__intro">Pick your HoneyBook plan, how you pay and how many people use it.</p>

    <div class="hbc__inputs">
      <label class="hbc__field">
        <span>Your HoneyBook plan</span>
        <select v-model="honeybookPlan" translate="no">
          <option v-for="tier in HONEYBOOK_TIERS" :key="tier" :value="tier">{{ tier }}</option>
        </select>
      </label>
      <fieldset class="hbc__field hbc__billing">
        <legend>Billing</legend>
        <div class="hbc__segments">
          <label :class="{ 'is-active': billing === 'monthly' }">
            <input v-model="billing" type="radio" name="hbc-billing" value="monthly" />
            <span>Monthly</span>
          </label>
          <label :class="{ 'is-active': billing === 'yearly' }">
            <input v-model="billing" type="radio" name="hbc-billing" value="yearly" />
            <span>Yearly</span>
          </label>
        </div>
      </fieldset>
      <label class="hbc__field">
        <span>People on the team</span>
        <input v-model.number="teamSize" type="number" inputmode="numeric" min="1" max="500" step="1" @change="teamSize = result.teamSize" />
      </label>
      <label class="hbc__check">
        <input v-model="bookingOrExpenses" type="checkbox" />
        <span>I take client bookings or track expenses in HoneyBook</span>
      </label>
    </div>

    <div class="hbc__results">
      <div class="hbc__card">
        <p class="hbc__vendor">HoneyBook <span translate="no">{{ result.honeybook.plan }}</span></p>
        <p v-show="result.honeybook.yearly !== null" :aria-hidden="hiddenUnless(result.honeybook.yearly !== null)" class="hbc__amount">
          <strong translate="no">{{ money(result.honeybook.yearly) }}</strong> <span>per year</span>
        </p>
        <p v-show="result.honeybook.yearly === null" :aria-hidden="hiddenUnless(result.honeybook.yearly === null)" class="hbc__amount">
          <a href="https://www.honeybook.com/pricing" rel="noopener" target="_blank">See site</a>
        </p>
        <dl class="hbc__lines">
          <div>
            <dt>Per month</dt>
            <dd translate="no">{{ result.honeybook.perMonth === null ? '–' : money(result.honeybook.perMonth) }}</dd>
          </div>
        </dl>
      </div>
      <div class="hbc__card">
        <p class="hbc__vendor">Workspace369 <span translate="no">{{ result.workspace.plan.name }}</span></p>
        <p class="hbc__amount">
          <strong translate="no">{{ money(result.workspace.yearly) }}</strong> <span>per year</span>
        </p>
        <dl class="hbc__lines">
          <div>
            <dt>Seats included</dt>
            <dd translate="no">{{ result.workspace.plan.seats }}</dd>
          </div>
          <div v-show="result.workspace.extraSeats > 0" :aria-hidden="hiddenUnless(result.workspace.extraSeats > 0)">
            <dt>Extra seats at $10 a month each</dt>
            <dd translate="no">{{ result.workspace.extraSeats }}</dd>
          </div>
        </dl>
      </div>
    </div>

    <p class="hbc__verdict" :class="verdictClass" aria-live="polite" aria-atomic="true">
      <span v-show="verdict === 'less'" :aria-hidden="hiddenUnless(verdict === 'less')"><strong>Workspace369 costs less.</strong> <span>Yearly difference:</span> <strong translate="no">{{ verdict === 'less' ? savings : '' }}</strong></span>
      <span v-show="verdict === 'more'" :aria-hidden="hiddenUnless(verdict === 'more')"><strong>Workspace369 costs more.</strong> <span>Yearly difference:</span> <strong translate="no">{{ verdict === 'more' ? savings : '' }}</strong></span>
      <span v-show="verdict === 'same'" :aria-hidden="hiddenUnless(verdict === 'same')"><strong>Both cost the same per year.</strong></span>
      <span v-show="verdict === 'unknown'" :aria-hidden="hiddenUnless(verdict === 'unknown')"><strong>See honeybook.com/pricing for this plan's price.</strong></span>
    </p>

    <ul class="hbc__notes">
      <li v-show="movedUp === 'Starter-Essentials'" :aria-hidden="hiddenUnless(movedUp === 'Starter-Essentials')">HoneyBook Starter can't add team members, so this uses Essentials.</li>
      <li v-show="movedUp === 'Starter-Premium'" :aria-hidden="hiddenUnless(movedUp === 'Starter-Premium')">HoneyBook Starter can't add team members and Essentials adds up to 2, so this uses Premium.</li>
      <li v-show="movedUp === 'Essentials-Premium'" :aria-hidden="hiddenUnless(movedUp === 'Essentials-Premium')">HoneyBook Essentials adds up to 2 team members, so this uses Premium.</li>
      <li v-show="ownerAssumption" :aria-hidden="hiddenUnless(ownerAssumption)">This assumes the owner is not counted in HoneyBook's 2 team members. HoneyBook does not state a total headcount.</li>
      <li v-show="match === 'cadet'" :aria-hidden="hiddenUnless(match === 'cadet')">Matched plan: Cadet, the solo plan, like HoneyBook Starter.</li>
      <li v-show="match === 'voyager'" :aria-hidden="hiddenUnless(match === 'voyager')">Matched plan: Voyager, with team seats, like HoneyBook Essentials and Premium.</li>
      <li v-show="match === 'booking'" :aria-hidden="hiddenUnless(match === 'booking')">Matched plan: Specialist, because online booking and expenses start there.</li>
      <li v-show="match === 'cheapest'" :aria-hidden="hiddenUnless(match === 'cheapest')">Matched plan: the lowest-cost Workspace369 plan that fits your team.</li>
    </ul>

    <p class="hbc__general">HoneyBook charges per plan, not per person, and Premium has no team limit. Workspace369 adds $10 a month for each seat beyond the plan's included seats, so which one costs less depends on your team size.</p>

    <div class="hbc__actions">
      <a
        :href="addReferralToUrl(planSignupUrl(result.workspace.plan.slug, billing === 'yearly', founding.enabled))"
        class="hbc__primary"
        data-conversion-event="start_free_click"
        data-conversion-label="HoneyBook cost calculator trial"
        data-conversion-location="blog-honeybook-cost-calculator"
        :data-conversion-plan="result.workspace.plan.slug"
        :data-trial-billing="billing"
      >Start a 14-day trial</a>
      <!-- The terms sit directly under the trial button (B1.1), so the ghost link comes after them in the
           markup; on wider screens the grid puts it back beside the trial button. -->
      <TrialDisclosure :plan="result.workspace.plan.slug" :annual="billing === 'yearly'" />
      <NuxtLink
        :to="localePath('/switch/honeybook')"
        class="hbc__ghost"
        data-conversion-event="comparison_click"
        data-conversion-label="HoneyBook cost calculator switch"
        data-conversion-location="blog-honeybook-cost-calculator"
      >See how to switch from HoneyBook</NuxtLink>
    </div>

    <!-- One line on purpose: Vue drops whitespace between elements that sits on its own line. -->
    <p class="hbc__source"><span>HoneyBook prices checked</span> <time translate="no" :datetime="honeybook.checkedOn">{{ checkedOnLabel }}</time> <span>on</span> <a href="https://www.honeybook.com/pricing" rel="noopener" target="_blank" translate="no">honeybook.com/pricing</a>. <span>Workspace369 prices are from our</span> <NuxtLink :to="localePath('/pricing')">pricing page</NuxtLink>. <span>List prices before tax. Add-ons are not included.</span></p>
  </section>
</template>

<script setup lang="ts">
import priceSources from '~/data/competitor-price-sources.json';
import pricing from '~/data/pricing-features.json';
import { planSignupUrl } from '~/data/founding-offer';
import {
  HONEYBOOK_TIERS,
  compareCosts,
  honeybookPrices,
  workspacePlans,
  type Billing,
  type HoneybookTier,
} from '~/utils/honeybookCost';

const { addReferralToUrl } = useReferral();
// Same sign-up link as the plan cards: plan, billing period and, while it runs, the founding promo
// that the yearly TrialDisclosure line names.
const founding = useFoundingOffer();
const { localePath } = useWorkspaceLocale();

const honeybook = honeybookPrices(priceSources as Parameters<typeof honeybookPrices>[0]);
const plans = workspacePlans(pricing as Parameters<typeof workspacePlans>[0]);

// Server-rendered worked example: a solo account on Starter, billed yearly (HoneyBook's default),
// the like-for-like solo case (Web LEAD, 2026-10-31). Every other input shows its real result,
// including when Workspace369 costs more.
const honeybookPlan = ref<HoneybookTier>('Starter');
const billing = ref<Billing>('yearly');
const teamSize = ref(1);
const bookingOrExpenses = ref(false);

const result = computed(() =>
  compareCosts(
    { honeybookPlan: honeybookPlan.value, billing: billing.value, teamSize: teamSize.value, bookingOrExpenses: bookingOrExpenses.value },
    honeybook,
    plans,
  ),
);

const verdict = computed(() => {
  const difference = result.value.difference;
  if (difference === null) return 'unknown';
  if (difference > 0) return 'less';
  if (difference < 0) return 'more';
  return 'same';
});
const savings = computed(() => money(Math.abs(result.value.difference || 0)));

// Lines for other inputs render hidden (for translation) and aria-hidden, which also keeps them
// out of llms-full (scripts/generate-llms-full.mjs drops aria-hidden subtrees): AI tools read
// only the worked example, never "costs less" and "costs more" side by side.
const hiddenUnless = (shown: boolean) => (shown ? undefined : 'true');

const verdictClass = computed(() => `hbc__verdict--${verdict.value}`);

const movedUp = computed(() => (result.value.honeybook.movedUp ? `${honeybookPlan.value}-${result.value.honeybook.plan}` : ''));
const ownerAssumption = computed(() => result.value.honeybook.plan === 'Essentials' && result.value.teamSize === 3);

const match = computed(() => {
  const slug = result.value.workspace.plan.slug;
  if (bookingOrExpenses.value && slug === 'specialist') return 'booking';
  if (!bookingOrExpenses.value && slug === 'cadet') return 'cadet';
  if (!bookingOrExpenses.value && slug === 'voyager') return 'voyager';
  return 'cheapest';
});

// Fixed en-US formatting so the server and client render the same text.
const money = (value: number | null) =>
  value === null
    ? ''
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(value);

const checkedOnLabel = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(
  new Date(`${honeybook.checkedOn}T00:00:00Z`),
);
</script>

<style scoped>
.hbc { margin: 2rem 0; padding: 1.25rem; border: 1px solid #dbe4ec; border-radius: 0.5rem; background: #f4f8fa; color: #0f172a; }
.hbc .hbc__title { margin: 0; color: #0f172a; font-size: 1.1rem; font-weight: 850; line-height: 1.3; }
.hbc .hbc__intro { margin: 0.35rem 0 0; color: #334155; font-size: 0.9375rem; line-height: 1.55; }

.hbc .hbc__inputs { display: grid; gap: 0.9rem; margin-top: 1rem; }
@media (min-width: 640px) { .hbc .hbc__inputs { grid-template-columns: 1fr 1fr 1fr; align-items: end; } .hbc .hbc__check { grid-column: 1 / -1; } }

.hbc .hbc__field { display: grid; gap: 0.35rem; min-width: 0; margin: 0; border: 0; padding: 0; }
.hbc .hbc__field > span, .hbc .hbc__field legend { color: #0f172a; font-size: 0.8125rem; font-weight: 700; }
.hbc .hbc__field legend { margin-bottom: 0.35rem; padding: 0; }
.hbc select, .hbc input[type="number"] { width: 100%; min-height: 44px; border: 1px solid #94a3b8; border-radius: 0.375rem; background: #fff; padding: 0 0.65rem; color: #0f172a; font-size: 1rem; }
.hbc select:focus-visible, .hbc input:focus-visible { outline: 3px solid #269dbb; outline-offset: 2px; }

.hbc .hbc__segments { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #94a3b8; border-radius: 0.375rem; overflow: hidden; background: #fff; }
.hbc .hbc__segments label { position: relative; display: flex; align-items: center; justify-content: center; min-height: 44px; color: #334155; font-size: 0.9375rem; font-weight: 650; cursor: pointer; }
.hbc .hbc__segments label + label { border-left: 1px solid #94a3b8; }
.hbc .hbc__segments label.is-active { background: #0f172a; color: #fff; }
.hbc .hbc__segments input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.hbc .hbc__segments label:has(input:focus-visible) { outline: 3px solid #269dbb; outline-offset: -3px; }

.hbc .hbc__check { display: flex; align-items: flex-start; gap: 0.55rem; color: #334155; font-size: 0.9375rem; line-height: 1.45; cursor: pointer; }
.hbc .hbc__check input { flex: none; width: 1.15rem; height: 1.15rem; margin-top: 0.15rem; accent-color: #0f172a; }

.hbc .hbc__results { display: grid; gap: 0.75rem; margin-top: 1.25rem; }
@media (min-width: 560px) { .hbc .hbc__results { grid-template-columns: 1fr 1fr; } }
.hbc .hbc__card { border: 1px solid #dbe4ec; border-radius: 0.5rem; background: #fff; padding: 0.9rem 1rem; }
.hbc .hbc__vendor { margin: 0; color: #475569; font-size: 0.8125rem; font-weight: 700; }
.hbc .hbc__amount { margin: 0.25rem 0 0; color: #334155; font-size: 0.9375rem; }
.hbc .hbc__amount strong { color: #0f172a; font-size: 1.5rem; font-weight: 850; font-variant-numeric: tabular-nums; }
.hbc .hbc__amount a { color: #0f172a; font-size: 1.1rem; font-weight: 800; text-decoration: underline; }
.hbc .hbc__lines { display: grid; gap: 0.25rem; margin: 0.6rem 0 0; color: #475569; font-size: 0.8125rem; }
.hbc .hbc__lines div { display: flex; justify-content: space-between; gap: 0.75rem; border-top: 1px solid #eef2f6; padding-top: 0.25rem; }
.hbc .hbc__lines dd { margin: 0; color: #0f172a; font-weight: 700; font-variant-numeric: tabular-nums; }

.hbc .hbc__verdict { margin: 1rem 0 0; border-radius: 0.375rem; padding: 0.7rem 0.85rem; background: #fff; color: #0f172a; font-size: 1rem; line-height: 1.45; }
.hbc .hbc__verdict--less { background: #ecfdf5; color: #065f46; }
.hbc .hbc__verdict--more { background: #fff7ed; color: #9a3412; }

.hbc .hbc__notes { display: grid; gap: 0.3rem; margin: 0.75rem 0 0; padding: 0 0 0 1.1rem; color: #334155; font-size: 0.875rem; line-height: 1.5; list-style: disc; }
.hbc .hbc__notes li { margin: 0; }

.hbc .hbc__general { margin: 0.75rem 0 0; color: #475569; font-size: 0.875rem; line-height: 1.5; }
.hbc .hbc__actions { display: flex; flex-direction: column; gap: 0.75rem; margin-top: 1.1rem; }
.hbc .hbc__primary, .hbc .hbc__ghost { display: flex; justify-content: center; align-items: center; min-height: 44px; padding: 0.5rem 1rem; border-radius: 0.375rem; font-size: 0.9375rem; font-weight: 600; text-align: center; text-decoration: none; }
@media (min-width: 640px) {
  .hbc .hbc__actions { display: grid; grid-template-columns: auto auto; justify-content: start; gap: 0 0.75rem; }
  .hbc .hbc__primary { grid-area: 1 / 1; }
  .hbc .hbc__ghost { grid-area: 1 / 2; }
  .hbc .hbc__actions > :deep(.trial-disclosure) { grid-column: 1 / -1; }
}
.hbc .hbc__primary, .hbc .hbc__primary:hover { background: #0f172a; color: #fff; }
.hbc .hbc__primary:hover { background: #1e293b; }
.hbc .hbc__ghost, .hbc .hbc__ghost:hover { border: 1px solid #94a3b8; color: #0f172a; }
.hbc .hbc__ghost:hover { border-color: #0f172a; }
.hbc .hbc__primary:focus-visible, .hbc .hbc__ghost:focus-visible { outline: 3px solid #269dbb; outline-offset: 3px; }
/* TrialDisclosure has more than one root, so reach it with :deep (as InlineCta does). */
.hbc :deep(.trial-disclosure) { margin: -0.2rem 0 0; }
@media (min-width: 640px) { .hbc :deep(.trial-disclosure) { margin: 0.55rem 0 0; } }

.hbc .hbc__source { margin: 1rem 0 0; color: #475569; font-size: 0.8125rem; line-height: 1.55; }
.hbc .hbc__source a { color: #0f172a; text-decoration: underline; }

.dark .hbc { background: #132333; border-color: #364859; color: #edf5fa; }
.dark .hbc .hbc__title, .dark .hbc .hbc__field > span, .dark .hbc .hbc__field legend, .dark .hbc .hbc__amount strong, .dark .hbc .hbc__lines dd, .dark .hbc .hbc__amount a, .dark .hbc .hbc__source a { color: #f8fafc; }
.dark .hbc .hbc__intro, .dark .hbc .hbc__general, .dark .hbc .hbc__check, .dark .hbc .hbc__notes, .dark .hbc .hbc__amount, .dark .hbc .hbc__vendor, .dark .hbc .hbc__lines, .dark .hbc .hbc__source { color: #b8c7d6; }
.dark .hbc select, .dark .hbc input[type="number"] { border-color: #52667a; background: #0b1622; color: #f8fafc; }
.dark .hbc .hbc__segments { border-color: #52667a; background: #0b1622; }
.dark .hbc .hbc__segments label { color: #b8c7d6; }
.dark .hbc .hbc__segments label + label { border-left-color: #52667a; }
.dark .hbc .hbc__segments label.is-active { background: #edf5fa; color: #0b1622; }
.dark .hbc .hbc__check input { accent-color: #67e8f9; }
.dark .hbc .hbc__card { border-color: #364859; background: #0b1622; }
.dark .hbc .hbc__lines div { border-top-color: #22364a; }
.dark .hbc .hbc__verdict { background: #0b1622; color: #f8fafc; }
.dark .hbc .hbc__verdict--less { background: rgba(16, 185, 129, 0.14); color: #a7f3d0; }
.dark .hbc .hbc__verdict--more { background: rgba(249, 115, 22, 0.14); color: #fed7aa; }
.dark .hbc .hbc__primary, .dark .hbc .hbc__primary:hover { background: #edf5fa; color: #0b1622; }
.dark .hbc .hbc__ghost, .dark .hbc .hbc__ghost:hover { border-color: #52667a; color: #edf5fa; }
</style>
